import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar, { clearSession } from "../components/Sidebar";
import { decisionInfo, enumOf, methodLabel, splitWords, subjectTypeLabel } from "../utils/enums";
import { getLogVehicle } from "../utils/accessLogs";
import {
  Dashboard6Styles, PagePreloader, PageHeader, CardHead, SegTabs, StatusChip, Pagination,
} from "../components/ModalUI";

// ==========================================
// CONSTANTS & HTTP HELPERS
// ==========================================
const API_BASE = "https://zangon-web-api.azurewebsites.net";
const SESSION_KEY = "zangon_session";
const PAGE_SIZE = 15;

const safeJsonParse = (text) => { try { return JSON.parse(text); } catch { return null; } };
const getSession = () => { try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; } };

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally {
    clearTimeout(id);
  }
}

async function refreshAccessToken() {
  const refreshToken = getSession()?.refreshToken;
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const json = safeJsonParse(await res.text());
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  localStorage.setItem(SESSION_KEY, JSON.stringify(next));
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  const headers = { Accept: "application/json", Authorization: `Bearer ${getSession()?.accessToken || ""}`, ...(options.headers || {}) };
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      headers.Authorization = `Bearer ${renewed.accessToken}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

// Builds a query string from defined, non-empty values.
const toQuery = (params) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") q.append(k, v); });
  return q.toString();
};

// ==========================================
// FORMATTING
// ==========================================
const fmtWhen = (iso) =>
  iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
const dayStart = (d) => (d ? new Date(`${d}T00:00:00`).toISOString() : undefined);
const dayEnd = (d) => (d ? new Date(`${d}T23:59:59.999`).toISOString() : undefined);
const decisionColor = (d = "") => (/allow|grant|success|approv/i.test(d) ? "#16A34A" : /den|reject|block|fail/i.test(d) ? "#DC2626" : "#a1a1aa");
const prettyJson = (text) => { if (!text) return ""; try { return JSON.stringify(JSON.parse(text), null, 2); } catch { return String(text); } };

// ==========================================
// SHARED LOG HOOK + FILTER BAR
// ==========================================
function useLog(path, params, errorLabel) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const key = JSON.stringify(params);

  useEffect(() => { setPage(1); }, [key]);

  useEffect(() => {
    const estateId = getSession()?.estateId || getSession()?.buildingId;
    if (!estateId) { setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await authFetch(`${API_BASE}/estates/${estateId}/${path}?${toQuery({ ...JSON.parse(key), pageIndex: page, pageSize: PAGE_SIZE })}`);
        const json = safeJsonParse(await res.text());
        if (!res.ok) throw new Error(json?.message || errorLabel);
        const list = Array.isArray(json?.data?.data) ? json.data.data : Array.isArray(json?.data) ? json.data : [];
        if (cancelled) return;
        setRows(list);
        setTotal(json?.data?.totalCount ?? list.length);
      } catch (err) {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        toast.error(err.message || errorLabel);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [path, key, page, errorLabel]);

  return { rows, total, page, setPage, loading, totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)) };
}

function FilterBar({ filters, setFilters, fields }) {
  const [draft, setDraft] = useState(filters);
  const submit = (e) => { e.preventDefault(); setFilters(draft); };
  const reset = () => { const empty = Object.fromEntries(fields.map((f) => [f.key, ""])); setDraft(empty); setFilters(empty); };
  return (
    <form onSubmit={submit} className="d-flex flex-wrap align-items-end gap-3 mb-4">
      {fields.map((f) => (
        <div key={f.key} className="d6m-field" style={{ minWidth: f.type === "date" ? 150 : 180 }}>
          <label>{f.label}</label>
          <input type={f.type || "text"} className="d6m-input" placeholder={f.placeholder} value={draft[f.key] || ""} onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} />
        </div>
      ))}
      <button type="submit" className="btn btn-success d6-mbtn d6-mbtn-sm mb-1">Apply</button>
      <button type="button" className="btn btn-light d6-mbtn d6-mbtn-sm mb-1" onClick={reset}>Clear</button>
    </form>
  );
}

// ==========================================
// AUDIT LOG   GET /estates/{id}/audit-logs
// ==========================================
const AUDIT_FIELDS = [
  { key: "search", label: "Search", placeholder: "Actor, entity…" },
  { key: "action", label: "Action", placeholder: "e.g. Create" },
  { key: "entityName", label: "Entity", placeholder: "e.g. Tenant" },
  { key: "from", label: "From", type: "date" },
  { key: "to", label: "To", type: "date" },
];

function AuditLogPanel() {
  const [filters, setFilters] = useState({ search: "", action: "", entityName: "", from: "", to: "" });
  const [open, setOpen] = useState(null);
  const params = { search: filters.search, action: filters.action, entityName: filters.entityName, startDate: dayStart(filters.from), endDate: dayEnd(filters.to), sortBy: "occurredAt desc" };
  const { rows, total, page, setPage, loading, totalPages } = useLog("audit-logs", params, "Failed to load the audit log.");

  return (
    <div className="d6-card p-6 mb-5">
      <CardHead title="Audit Log" subtitle={`${total} recorded change${total === 1 ? "" : "s"} — who did what, and when`} className="mb-4" />
      <FilterBar filters={filters} setFilters={setFilters} fields={AUDIT_FIELDS} />
      {loading ? (
        <div className="d6m-empty py-8">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="d6m-empty py-8">No audit entries match.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle table-row-dashed fs-7 gy-3 mb-0">
            <thead>
              <tr className="text-gray-500 fw-bold fs-8 text-uppercase"><th>When</th><th>Actor</th><th>Action</th><th>Entity</th><th>Request</th><th>IP</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr style={{ cursor: r.changesJson ? "pointer" : "default" }} onClick={() => r.changesJson && setOpen(open === r.id ? null : r.id)}>
                    <td className="text-nowrap">{fmtWhen(r.occurredAt)}</td>
                    <td><div className="fw-bold text-gray-900 text-truncate" style={{ maxWidth: 220 }}>{r.actorEmail || r.actorId || "—"}</div><div className="d6m-muted">{r.actorType}</div></td>
                    <td><StatusChip color="#9C5432">{r.action || "—"}</StatusChip></td>
                    <td><div className="fw-semibold">{r.entityName || "—"}</div><div className="d6m-muted text-truncate" style={{ maxWidth: 160 }}>{r.entityId}</div></td>
                    <td><span className="fw-bold">{r.httpMethod}</span> <span className="d6m-muted">{r.requestPath}</span></td>
                    <td className="text-nowrap">{r.ipAddress || "—"}</td>
                  </tr>
                  {open === r.id && (
                    <tr>
                      <td colSpan={6}>
                        <pre className="d6m-invoice mb-0 fs-8" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{prettyJson(r.changesJson)}</pre>
                        {r.correlationId && <div className="d6m-muted mt-2">Correlation ID: {r.correlationId}</div>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {totalPages > 1 && <div className="mt-4"><Pagination page={page} totalPages={totalPages} onChange={setPage} summary={`${total} entries`} /></div>}
    </div>
  );
}

// ==========================================
// CARD TAPS   GET /estates/{id}/building-nfc/access-logs
// ==========================================
const NFC_FIELDS = [
  { key: "search", label: "Search", placeholder: "Name, house, card…" },
  { key: "accessPoint", label: "Access point", placeholder: "e.g. Main Gate" },
  { key: "from", label: "From", type: "date" },
  { key: "to", label: "To", type: "date" },
];

function NfcTapPanel() {
  const [filters, setFilters] = useState({ search: "", accessPoint: "", from: "", to: "" });
  const params = { search: filters.search, accessPoint: filters.accessPoint, startDate: dayStart(filters.from), endDate: dayEnd(filters.to), sortBy: "loggedAt desc" };
  const { rows, total, page, setPage, loading, totalPages } = useLog("building-nfc/access-logs", params, "Failed to load card taps.");

  return (
    <div className="d6-card p-6 mb-5">
      <CardHead title="Card Tap Log" subtitle={`${total} NFC tap${total === 1 ? "" : "s"} recorded at access points`} className="mb-4" />
      <FilterBar filters={filters} setFilters={setFilters} fields={NFC_FIELDS} />
      {loading ? (
        <div className="d6m-empty py-8">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="d6m-empty py-8">No card taps match.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle table-row-dashed fs-7 gy-3 mb-0">
            <thead>
              <tr className="text-gray-500 fw-bold fs-8 text-uppercase"><th>When</th><th>Holder</th><th>Building</th><th>Card</th><th>Direction</th><th>Decision</th><th>Access point</th><th>Vehicle</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const holder = r.tenantName || r.primaryOccupantName || r.subjectType || "—";
                const phone = r.tenantPhoneNumber || r.primaryOccupantPhoneNumber;
                const v = getLogVehicle(r);
                return (
                  <tr key={r.id}>
                    <td className="text-nowrap">{fmtWhen(r.loggedAt)}</td>
                    <td><div className="fw-bold text-gray-900 text-truncate" style={{ maxWidth: 200 }}>{holder}</div><div className="d6m-muted">{[enumOf(r, "subjectType") ? subjectTypeLabel(enumOf(r, "subjectType")) : null, phone].filter(Boolean).join(" · ")}</div></td>
                    <td>{r.buildingNumber || "—"}<div className="d6m-muted">{[r.phaseName, r.typeName].filter(Boolean).join(" · ")}</div></td>
                    <td className="text-nowrap">{r.codeLastFour ? `•••• ${r.codeLastFour}` : "—"}<div className="d6m-muted">{enumOf(r, "verificationMethod") !== undefined ? methodLabel(enumOf(r, "verificationMethod")) : ""}</div></td>
                    <td>{splitWords(enumOf(r, "direction")) || "—"}</td>
                    <td><StatusChip color={decisionColor(decisionInfo(enumOf(r, "decision")).label)}>{decisionInfo(enumOf(r, "decision")).label}</StatusChip>{r.decisionReason && <div className="d6m-muted mt-1">{r.decisionReason}</div>}</td>
                    <td>{r.accessPoint || "—"}<div className="d6m-muted">{r.checkedByName}</div></td>
                    <td>{v ? (
                      <>
                        <span className="fw-bold">{v.plate || (v.isUnlisted ? "Unlisted vehicle" : "Vehicle")}</span>
                        <div className="d6m-muted">{[[v.brand, v.model].filter(Boolean).join(" "), v.color].filter(Boolean).join(" · ")}{v.isUnlisted && v.plate ? " · unlisted" : ""}</div>
                      </>
                    ) : splitWords(enumOf(r, "accessMode")) || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {totalPages > 1 && <div className="mt-4"><Pagination page={page} totalPages={totalPages} onChange={setPage} summary={`${total} taps`} /></div>}
    </div>
  );
}

// ==========================================
// PAGE
// ==========================================
export default function AuditLog() {
  const [pageReady, setPageReady] = useState(false);
  const [tab, setTab] = useState("audit");

  useEffect(() => { const t = setTimeout(() => setPageReady(true), 300); return () => clearTimeout(t); }, []);

  useEffect(() => {
    const body = document.getElementById("kt_app_body") || document.body;
    body.id = "kt_app_body";
    body.className = "app-default";
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
      window.KTSwapper?.init?.();
    };
    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      <Dashboard6Styles />
      <PagePreloader visible={!pageReady} />
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          <div id="kt_app_header" className="app-header" data-kt-sticky="true" data-kt-sticky-activate="{default: false, lg: true}" data-kt-sticky-name="app-header-sticky" data-kt-sticky-offset="{default: false, lg: '300px'}">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2" />
                </div>
                <Link to="/dashboard"><img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" /></Link>
              </div>
              <PageHeader breadcrumb="Audit Log" title="Audit & Card Tap Logs" />
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="mb-5">
                      <SegTabs
                        value={tab}
                        onChange={setTab}
                        options={[
                          { value: "audit", label: "Audit Log" },
                          { value: "taps", label: "Card Taps" },
                        ]}
                      />
                    </div>
                    {tab === "audit" ? <AuditLogPanel /> : <NfcTapPanel />}
                    <div className="my-10"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
